// =====================================================================
// src/lib/relatorio_proprietario.js — o extrato do proprietário, sem DOM
//   (MdM `src/lib/calc/relatorio-fin-proprietario.js` · `abrirSeletor
//    RelatorioFinanceiro` 24466 · `gerarRelatorioFinanceiroPorProp` 24531 ·
//    `_formulasFinanceiroFooter` 24650 · exportações 24666–24848)
//
// É o relatório que o Hugo manda todo mês para cada proprietário: bruto
// pro-rata por noite, receitas avulsas, despesas diretas (lançamentos +
// despesas variáveis), taxa de administração e o líquido — e o perfil
// "Geral (Holding)", que DELEGA para `resultado()` (lib/financeiro.js)
// para a tela Financeiro e o extrato não poderem divergir.
//
// A única mudança de conta é a mesma da 0049: a taxa deixa de ser nome
// (FerTat = 2.800, Monteiro = 0, terceiros = 25%) e passa a ser o ACORDO do
// proprietário (`acordos_da_imobiliaria`) — `acordoDe` + `repasse`. Para
// a Monteiro o resultado é idêntico, porque o seed da 0049 gravou as três
// regras. Os "Critérios de cálculo" do rodapé nascem dos acordos que
// existem, não de um texto fixo.
//
// FONTE ÚNICA (regra do MdM de 07/09/2026, mantida): `lancamentos` já sai
// filtrado e é a única lista que card, tabela, PDF e Excel leem;
// `lancamentosTodos` é só para a aba de auditoria do Excel.
// =====================================================================
import { periodoDe, receitaNoPeriodo, acordoDe, repasse, resultado, MESES } from './financeiro.js';
import { noites } from './reservas.js';

const num = v => { const n = parseFloat(v); return Number.isFinite(n) ? n : 0; };
export const GERAL = '__geral';
export const SEM_PROPRIETARIO = '__sem';

// os perfis do seletor (24475): Geral primeiro, depois um por proprietário
// com flat na imobiliária, em ordem de nome
export function perfis(unidades, proprietarios) {
  const nomes = new Map((proprietarios || []).map(p => [p.id, p.nome]));
  const ids = new Set();
  let semDono = false;
  for (const u of unidades || []) { if (u.proprietario_id) { ids.add(u.proprietario_id); if (!nomes.has(u.proprietario_id) && u.proprietarios) nomes.set(u.proprietario_id, u.proprietarios.nome); } else semDono = true; }
  const lista = [{ chave: GERAL, rotulo: 'Geral (Holding)', descricao: 'Receita, despesas e resultado da holding', cor: '#1B3A7A', icone: 'ic-bank' }];
  for (const id of [...ids].sort((a, b) => String(nomes.get(a) || '').localeCompare(String(nomes.get(b) || ''), 'pt-BR'))) {
    lista.push({ chave: id, rotulo: nomes.get(id) || 'Proprietário', descricao: 'Receita, taxa de administração e líquido', cor: '#7c3aed', icone: 'ic-flats' });
  }
  if (semDono) lista.push({ chave: SEM_PROPRIETARIO, rotulo: 'Sem proprietário', descricao: 'Flats sem proprietário cadastrado', cor: '#6b7280', icone: 'ic-flats' });
  return lista;
}
// a descrição do card pelo acordo (24479): próprio / cota fixa / percentual
export function descricaoDoPerfil(perfil, acordos, moeda = v => String(v)) {
  if (perfil.chave === GERAL || perfil.chave === SEM_PROPRIETARIO) return perfil.descricao;
  const a = acordoDe(acordos, perfil.chave);
  if (!a) return 'Sem acordo cadastrado — o líquido sai sem taxa';
  if (a.tipo === 'proprio') return 'Receita, despesas e líquido dos flats próprios';
  if (a.tipo === 'cota_fixa') return `Receita, cota fixa (${moeda(a.valor)}/mês) e líquido dos flats`;
  return `Receita, taxa de administração (${num(a.valor)}%) e líquido`;
}
export function corDoPerfil(perfil, acordos) {
  if (perfil.chave === GERAL) return '#1B3A7A';
  const a = acordoDe(acordos, perfil.chave);
  if (a && a.tipo === 'proprio') return '#1B3A7A';
  if (a && a.tipo === 'cota_fixa') return '#0f766e';
  return perfil.cor || '#7c3aed';
}

// o rótulo da taxa (24551): a cota fixa aparece pelo nome que o acordo der
// (no MdM, "Lavanderia" para a FerTat); sem observação, "Cota fixa"
export function rotuloDaTaxa(a) {
  if (!a) return 'Taxa de Administração';
  if (a.tipo === 'cota_fixa') return (a.observacao && a.observacao.length <= 30 ? a.observacao : 'Cota fixa');
  return 'Taxa de Administração';
}

// ── a conta (`_calcRelatorioFinPorProp`) ─────────────────────────────
// dados = { unidades, reservas, valorDe, lancamentos, despesas, acordos, condominios }
export function extrato(dados, prop, ano, mes) {
  const periodo = periodoDe(ano, mes);
  const valorDe = dados.valorDe || (() => null);
  const acordos = dados.acordos || [];
  const nomeMes = `${MESES[Number(mes) - 1]}/${ano}`;
  const geral = prop === GERAL;
  const flats = (dados.unidades || []).filter(u => geral || (prop === SEM_PROPRIETARIO ? !u.proprietario_id : u.proprietario_id === prop));
  const ids = new Set(flats.map(u => u.id));

  // receita pro-rata por noite: toda reserva com pelo menos uma noite no mês
  let bruto = 0, noitesTotal = 0;
  const reservas = [];
  for (const r of dados.reservas || []) {
    if (!ids.has(r.unidade_id)) continue;
    const dentro = receitaNoPeriodo(r, periodo, () => 1);
    if (dentro <= 0) continue;
    const total = Math.max(1, noites(r.check_in, r.check_out));
    const nDentro = Math.round(dentro * total);
    const receita = receitaNoPeriodo(r, periodo, valorDe);
    reservas.push({ ...r, _noitesDentro: nDentro, _receitaPro: receita });
    bruto += receita; noitesTotal += nDentro;
  }

  // lançamentos do mês: do proprietário, só os dos flats dele; sem flat, só no Geral
  const lancamentosTodos = (dados.lancamentos || []).filter(l => {
    if (!l.data || l.data < periodo.ini || l.data > periodo.fim) return false;
    if (!geral) { if (!l.unidade_id || !ids.has(l.unidade_id)) return false; }
    return true;
  });
  const ehEspelho = l => (l.categoria === 'diarias' || l.categoria === 'aluguel') && !!l.reserva_id;   // "[res:" do MdM
  const ehFrigobar = l => l.categoria === 'frigobar';
  const entraNoExtrato = l => !ehFrigobar(l) && !(l.tipo === 'receita' && ehEspelho(l));
  const lancamentos = lancamentosTodos.filter(entraNoExtrato);
  const receitaPorFlat = new Map(), despesaPorFlat = new Map();
  let receitasAvulsas = 0, despesasLancs = 0;
  for (const l of lancamentos) {
    const v = num(l.valor);
    if (l.tipo === 'receita') { receitasAvulsas += v; if (l.unidade_id) receitaPorFlat.set(l.unidade_id, (receitaPorFlat.get(l.unidade_id) || 0) + v); }
    else if (l.tipo === 'despesa') { despesasLancs += v; if (l.unidade_id) despesaPorFlat.set(l.unidade_id, (despesaPorFlat.get(l.unidade_id) || 0) + v); }
  }
  // despesas variáveis (outra tabela): do proprietário, as dos flats dele;
  // no Geral, só as dos flats PRÓPRIOS (as de terceiros são custo do dono)
  const proprios = new Set((dados.unidades || []).filter(u => { const a = u.proprietario_id ? acordoDe(acordos, u.proprietario_id) : null; return a && a.tipo === 'proprio'; }).map(u => u.id));
  const permitidas = geral ? proprios : ids;
  const despesasVariaveisLista = (dados.despesas || []).filter(d => d.unidade_id && d.data && d.data >= periodo.ini && d.data <= periodo.fim && permitidas.has(d.unidade_id));
  let despesasVariaveis = 0;
  for (const d of despesasVariaveisLista) { const v = num(d.valor); despesasVariaveis += v; despesasLancs += v; despesaPorFlat.set(d.unidade_id, (despesaPorFlat.get(d.unidade_id) || 0) + v); }

  // a taxa: pelo ACORDO (no MdM: FerTat 2.800 · Monteiro 0 · terceiros 25%)
  const acordo = geral || prop === SEM_PROPRIETARIO ? null : acordoDe(acordos, prop);
  let taxaAdm = 0, regraTaxa = '';
  if (geral) regraTaxa = 'participação da holding pelos acordos';
  else if (!acordo) regraTaxa = 'sem acordo cadastrado';
  else if (acordo.tipo === 'proprio') { taxaAdm = 0; regraTaxa = 'Sem taxa (flat próprio da imobiliária)'; }
  else if (acordo.tipo === 'cota_fixa') { taxaAdm = bruto > 0 ? repasse(acordo, bruto, 1) : 0; regraTaxa = `cota fixa por mês com atividade`; }
  else { taxaAdm = repasse(acordo, bruto, 1); regraTaxa = `${num(acordo.valor)}% sobre o faturamento bruto`; }
  let liquido = bruto + receitasAvulsas - despesasLancs - taxaAdm;

  // Geral (Holding) = o Resultado da Holding, e nada mais
  let rh = null;
  if (geral) {
    rh = resultado(dados, ano, mes);
    bruto = rh.receitaHolding; receitasAvulsas = 0; despesasLancs = rh.despesaSemLoja; taxaAdm = 0; liquido = rh.liquidoSemLoja;
  }

  const porFlat = flats.map(u => ({
    unidade_id: u.id, numero: u.numero,
    noites: reservas.filter(r => r.unidade_id === u.id).reduce((s, r) => s + r._noitesDentro, 0),
    receita: reservas.filter(r => r.unidade_id === u.id).reduce((s, r) => s + r._receitaPro, 0),
    despesas: despesaPorFlat.get(u.id) || 0,
  })).sort((a, b) => (parseInt(a.numero, 10) || 0) - (parseInt(b.numero, 10) || 0));

  return {
    prop, nomeMes, periodo, flats, reservas, lancamentos, lancamentosTodos, receitaPorFlat, despesaPorFlat, despesasVariaveisLista, despesasVariaveis,
    bruto, noites: noitesTotal, receitasAvulsas, despesasLancs, taxaAdm, regraTaxa, liquido, acordo, porFlat,
    ehHolding: geral, despLoja: rh ? rh.despLoja : 0, liquidoComLoja: rh ? rh.liquidoHolding : liquido,
  };
}

// ── os rótulos dos cards (24551–24580) ───────────────────────────────
export function rotulos(d, moeda) {
  const ehHold = d.ehHolding;
  const taxa = rotuloDaTaxa(d.acordo);
  const temAvulsas = !ehHold && Math.abs(d.receitasAvulsas || 0) > 0.005;
  let subLiquido;
  if (ehHold) subLiquido = 'receita − despesas (loja à parte)';
  else {
    const t = ['bruto']; if (temAvulsas) t.push('avulsas'); t.push('despesas'); if (Math.abs(d.taxaAdm || 0) > 0.005) t.push(taxa.toLowerCase());
    subLiquido = t[0] + t.slice(1).map((x, i) => (i === 0 && temAvulsas ? ' + ' : ' − ') + x).join('');
  }
  return {
    receita: ehHold ? 'RECEITA DA HOLDING' : 'FATURAMENTO BRUTO',
    subReceita: ehHold ? 'pelos acordos: próprios · cotas fixas · percentuais' : `${d.noites} noites`,
    despesa: ehHold ? 'DESPESAS (SEM A LOJA)' : 'DESPESAS DIRETAS',
    subDespesa: ehHold ? 'condomínio, funcionários, IPTU, variáveis' : 'condomínio, manutenção, reparos',
    taxa: taxa.toUpperCase(),
    subTaxa: d.acordo && d.acordo.tipo === 'cota_fixa' ? 'cota fixa' + (moeda ? ` ${moeda(d.acordo.valor)}/mês` : '') : d.regraTaxa,
    liquido: ehHold ? 'RESULTADO SEM A LOJA' : 'LÍQUIDO PRO PROPRIETÁRIO',
    subLiquido, temAvulsas,
  };
}

// ── os critérios de cálculo do rodapé (24650), a partir dos acordos ──
export function criterios(acordos, moeda = v => String(v)) {
  const linhas = [
    'CRITÉRIOS DE CÁLCULO:',
    '• Faturamento Bruto = soma das reservas confirmadas/concluídas no período (status ≠ cancelada), pro-rata por noite quando a reserva cruza o mês.',
    '• Receita Avulsa = lançamentos de receita feitos à mão no período. NÃO inclui as diárias das reservas: cada reserva já está no Faturamento Bruto (contá-la aqui somaria a mesma receita duas vezes).',
    '• Despesas Diretas = lançamentos de despesa do(s) flat(s) no período (condomínio, IPTU, etc.) + despesas variáveis (manutenção, reparos, móveis).',
  ];
  const padrao = (acordos || []).find(a => !a.proprietario_id);
  const porProp = (acordos || []).filter(a => a.proprietario_id);
  const texto = a => (a.tipo === 'proprio' ? 'sem taxa (flat próprio da imobiliária)' : a.tipo === 'cota_fixa' ? `cota fixa de ${moeda(a.valor)}/mês com atividade` : `${num(a.valor)}% do faturamento bruto`);
  for (const a of porProp) linhas.push(`• Taxa de Administração — ${a.proprietarios && a.proprietarios.nome ? a.proprietarios.nome : 'proprietário'}: ${texto(a)}${a.tipo === 'cota_fixa' && a.observacao ? ` (no relatório aparece como "${a.observacao}")` : ''}.`);
  if (padrao) linhas.push(`• Taxa de Administração — demais proprietários (regra padrão): ${texto(padrao)}.`);
  linhas.push(
    '• Líquido pro Proprietário = Faturamento Bruto + Receitas Avulsas − Despesas Diretas − Taxa de Administração.',
    '• Frigobar não entra neste extrato (nem a venda, nem a compra): é receita exclusiva da imobiliária e tem resultado próprio em Financeiro → Resultado.',
    '• Perfil "Geral (Holding)": não é a soma dos extratos. Mostra a RECEITA DA HOLDING pelos acordos e usa exatamente a mesma conta da tabela Resultado, com a loja isolada e o resultado em duas linhas (sem e com a loja).',
    '• Os valores consideram exclusivamente o período filtrado na tela Financeiro (Ano/Mês selecionados).');
  return linhas;
}

// ── o que vai para o PDF e para o Excel (24685–24847) ────────────────
export function resumoParaPdf(d, moeda) {
  const r = rotulos(d, moeda);
  const cor = v => (v >= 0 ? 'azul' : 'vermelho');
  if (d.ehHolding) return [
    { texto: `Receita da Holding:   ${moeda(d.bruto)}   (${d.regraTaxa})` },
    { texto: `Despesas (sem a loja): -${moeda(d.despesasLancs)}` },
    { texto: `RESULTADO SEM A LOJA: ${moeda(d.liquido)}`, negrito: true, cor: cor(d.liquido) },
    { texto: `Despesas da Loja:     -${moeda(d.despLoja)}   (centro de custo à parte)` },
    { texto: `LÍQUIDO DA HOLDING (com a loja): ${moeda(d.liquidoComLoja)}`, negrito: true, cor: cor(d.liquidoComLoja) },
  ];
  const linhas = [{ texto: `Faturamento Bruto:    ${moeda(d.bruto)} (${d.noites} noites)` }];
  if (d.receitasAvulsas > 0) linhas.push({ texto: `Receitas Avulsas:     ${moeda(d.receitasAvulsas)}` });
  linhas.push({ texto: `Despesas Diretas:    -${moeda(d.despesasLancs)}` });
  linhas.push({ texto: `${rotuloDaTaxa(d.acordo)}: ${moeda(d.taxaAdm)}   (${d.regraTaxa})` });
  linhas.push({ texto: `LÍQUIDO PRO PROPRIETÁRIO: ${moeda(d.liquido)}`, negrito: true, cor: cor(d.liquido) });
  return linhas;
}
export const COLUNAS_POR_FLAT = Object.freeze(['Flat', 'Noites', 'Receita', 'Despesas']);
export function linhasPorFlat(d, moeda) {
  return d.porFlat.map(f => ['Flat ' + (f.numero || '?'), String(f.noites), moeda(f.receita), f.despesas > 0 ? moeda(f.despesas) : '—']);
}
// as três abas do Excel: Resumo, Por Flat, Lançamentos (auditoria)
export function abasDaPlanilha(d, { rotuloPerfil, acordos, moeda, dataBR, quartoDe, rotuloDaCategoria }) {
  const gerado = `Período: ${d.nomeMes} · Gerado em ${new Date().toLocaleString('pt-BR')}`;
  const resumo = d.ehHolding
    ? [[`Relatório Financeiro — ${rotuloPerfil}`], [gerado], [''], ['Item', 'Valor'],
      [`Receita da Holding (${d.regraTaxa})`, d.bruto], ['Noites Ocupadas (todos os flats)', d.noites], ['Despesas (sem a loja)', -d.despesasLancs], ['RESULTADO SEM A LOJA', d.liquido],
      ['Despesas da Loja (centro de custo à parte)', -d.despLoja], ['LÍQUIDO DA HOLDING (com a loja)', d.liquidoComLoja], [''], ['CRITÉRIOS DE CÁLCULO']]
    : [[`Relatório Financeiro — ${rotuloPerfil}`], [gerado], [''], ['Item', 'Valor'],
      ['Faturamento Bruto', d.bruto], ['Noites Ocupadas', d.noites], ['Receitas Avulsas', d.receitasAvulsas], ['Despesas Diretas', -d.despesasLancs],
      [`${rotuloDaTaxa(d.acordo)} (${d.regraTaxa})`, -d.taxaAdm], ['LÍQUIDO PRO PROPRIETÁRIO', d.liquido], [''], ['CRITÉRIOS DE CÁLCULO']];
  for (const l of criterios(acordos, moeda).slice(1)) resumo.push([l]);
  const porFlat = [[...COLUNAS_POR_FLAT], ...d.porFlat.map(f => ['Flat ' + (f.numero || '?'), f.noites, Number(f.receita.toFixed(2)), Number(f.despesas.toFixed(2))])];
  const noExtrato = new Set(d.lancamentos.map(l => l.id));
  const lancs = [['Data', 'Descrição', 'Flat', 'Categoria', 'Tipo', 'Valor (R$)', 'No extrato?']];
  for (const l of d.lancamentosTodos) {
    const entra = noExtrato.has(l.id);
    const motivo = entra ? 'sim' : (l.categoria === 'frigobar' ? 'não — frigobar é resultado da holding' : 'não — diária já contada no Faturamento Bruto');
    lancs.push([dataBR(l.data), l.descricao || '-', l.unidade_id ? 'Flat ' + quartoDe(l.unidade_id) : 'Geral', rotuloDaCategoria(l.categoria), l.tipo || '-', num(l.valor), motivo]);
  }
  for (const v of d.despesasVariaveisLista) lancs.push([dataBR(v.data), v.observacao || v.tipo || 'Despesa variável', 'Flat ' + quartoDe(v.unidade_id), `Despesa variável (${v.tipo || '-'})`, 'despesa', num(v.valor), 'sim']);
  return [{ nome: 'Resumo', dados: resumo, larguras: [60, 20] }, { nome: 'Por Flat', dados: porFlat }, { nome: 'Lançamentos', dados: lancs }];
}
export function nomeDoRelatorio(rotuloPerfil, d) {
  return `Financeiro_${rotuloPerfil === 'Geral (Holding)' ? 'Geral' : String(rotuloPerfil).replace(/\s+/g, '_')}_${d.nomeMes.replace('/', '-')}`;
}
